class VyrloomImageViewer extends HTMLElement {
  connectedCallback() {
    this.controller = new AbortController();
    const signal = this.controller.signal;
    this.dialog = this.querySelector('dialog');
    this.stage = this.querySelector('[data-viewer-stage]');
    this.image = this.querySelector('[data-viewer-image]');
    this.pointers = new Map();
    this.scale = 1;
    this.x = 0;
    this.y = 0;
    document.addEventListener('pointerdown', (event) => {
      const trigger = event.target.closest?.('[data-vyrloom-zoom]');
      if (trigger && event.isPrimary && event.button === 0) {
        this.triggerGesture = { trigger, x: event.clientX, y: event.clientY, moved: false };
      } else if (this.triggerGesture) {
        this.triggerGesture.moved = true;
      }
    }, { signal, passive: true, capture: true });
    document.addEventListener('pointermove', (event) => {
      const gesture = this.triggerGesture;
      if (gesture && Math.hypot(event.clientX - gesture.x, event.clientY - gesture.y) > 10) {
        gesture.moved = true;
      }
    }, { signal, passive: true, capture: true });
    document.addEventListener('pointercancel', () => {
      if (this.triggerGesture) this.triggerGesture.moved = true;
    }, { signal, passive: true });
    document.addEventListener('click', (event) => this.handleTrigger(event), { signal });
    this.dialog.addEventListener('click', (event) => {
      const action = event.target.closest('[data-viewer-action]')?.dataset.viewerAction;
      if (action === 'close') this.dialog.close();
      if (action === 'previous') this.navigate(-1);
      if (action === 'next') this.navigate(1);
      if (action === 'in') this.setZoom(this.scale * 1.5);
      if (action === 'out') this.setZoom(this.scale / 1.5);
      if (action === 'reset') this.resetZoom();
    }, { signal });
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        event.preventDefault();
        this.navigate(event.key === 'ArrowLeft' ? -1 : 1);
      }
      if (event.key === '+' || event.key === '=') {
        event.preventDefault();
        this.setZoom(this.scale * 1.5);
      }
      if (event.key === '-') {
        event.preventDefault();
        this.setZoom(this.scale / 1.5);
      }
      if (event.key === '0') this.resetZoom();
    }, { signal });
    this.dialog.addEventListener('close', () => this.finish(), { signal });
    this.stage.addEventListener('wheel', (event) => {
      event.preventDefault();
      const bounds = this.stage.getBoundingClientRect();
      this.setZoom(this.scale * Math.exp(-event.deltaY * 0.002),
        event.clientX - bounds.left - bounds.width / 2,
        event.clientY - bounds.top - bounds.height / 2);
    }, { signal, passive: false });
    this.stage.addEventListener('pointerdown', (event) => this.pointerDown(event), { signal });
    this.stage.addEventListener('pointermove', (event) => this.pointerMove(event), { signal });
    this.stage.addEventListener('pointerup', (event) => this.pointerUp(event), { signal });
    this.stage.addEventListener('pointercancel', () => this.cancelGesture(), { signal });
    this.stage.addEventListener('lostpointercapture', () => this.cancelGesture(), { signal });
    this.image.addEventListener('load', () => {
      this.stage.removeAttribute('aria-busy');
      this.renderTransform();
    }, { signal });
    this.image.addEventListener('error', () => {
      this.stage.removeAttribute('aria-busy');
      this.querySelector('[data-viewer-error]').hidden = false;
    }, { signal });
    this.resizeObserver = new ResizeObserver(() => this.renderTransform());
    this.resizeObserver.observe(this.stage);
  }

  disconnectedCallback() {
    if (this.dialog?.open) this.dialog.close();
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }

  handleTrigger(event) {
    const trigger = event.target.closest?.('[data-vyrloom-zoom]');
    if (!trigger || !trigger.closest('media-gallery, .vyrloom-product-story')) return;
    if (this !== document.querySelector('vyrloom-image-viewer')) return;
    const dragged = event.detail !== 0 && this.triggerGesture?.moved;
    this.triggerGesture = null;
    event.preventDefault();
    if (dragged || this.dialog.open) return;
    const group = trigger.closest('media-gallery, .vyrloom-product-story');
    const sources = new Set();
    this.items = Array.from(group.querySelectorAll('[data-vyrloom-zoom]')).filter((item) => {
      const source = item.dataset.zoomSrc;
      if (!source || sources.has(source)) return false;
      sources.add(source);
      return true;
    });
    this.index = this.items.findIndex((item) => item.dataset.zoomSrc === trigger.dataset.zoomSrc);
    if (this.index < 0) return;
    this.opener = trigger;
    this.group = group;
    this.scrollPosition = { x: window.scrollX, y: window.scrollY };
    this.dialog.showModal();
    this.showImage();
    this.querySelector('[data-viewer-action="close"]').focus({ preventScroll: true });
  }

  showImage() {
    const item = this.items[this.index];
    this.cancelGesture();
    this.resetZoom(1.5);
    this.lastTap = null;
    this.querySelector('[data-viewer-error]').hidden = true;
    this.stage.setAttribute('aria-busy', 'true');
    this.image.alt = item.dataset.zoomAlt || '';
    const preview = item.parentElement.querySelector('img');
    const previewSource = preview?.currentSrc || preview?.getAttribute('data-src');
    const source = item.dataset.zoomSrc;
    const version = this.loadingVersion = (this.loadingVersion || 0) + 1;
    if (previewSource && !previewSource.startsWith('data:')) {
      this.image.src = previewSource;
      const fullImage = new Image();
      fullImage.onload = () => {
        if (version === this.loadingVersion && this.dialog.open) this.image.src = source;
      };
      fullImage.onerror = () => {
        if (version !== this.loadingVersion || !this.dialog.open) return;
        this.stage.removeAttribute('aria-busy');
        this.querySelector('[data-viewer-error]').hidden = false;
      };
      fullImage.src = source;
    } else {
      this.image.src = source;
    }
    this.querySelector('[data-viewer-original]').href = item.dataset.zoomSrc;
    this.querySelector('[data-viewer-counter]').textContent = `${this.index + 1} / ${this.items.length}`;
    for (const button of this.querySelectorAll('[data-viewer-action="previous"], [data-viewer-action="next"]')) {
      button.hidden = this.items.length < 2;
    }
  }

  navigate(direction) {
    if (this.items.length < 2) return;
    this.index = (this.index + direction + this.items.length) % this.items.length;
    this.showImage();
  }

  resetZoom(scale = 1) {
    this.scale = scale;
    this.x = 0;
    this.y = 0;
    this.renderTransform();
  }

  setZoom(value, focalX = 0, focalY = 0) {
    const scale = Math.min(5, Math.max(1, value));
    const ratio = scale / this.scale;
    this.x = focalX - (focalX - this.x) * ratio;
    this.y = focalY - (focalY - this.y) * ratio;
    this.scale = scale;
    this.renderTransform();
  }

  renderTransform() {
    const width = this.stage.clientWidth;
    const height = this.stage.clientHeight;
    const naturalWidth = this.image.naturalWidth || width;
    const naturalHeight = this.image.naturalHeight || height;
    const fit = Math.min(width / naturalWidth, height / naturalHeight);
    const limitX = Math.max(0, (naturalWidth * fit * this.scale - width) / 2);
    const limitY = Math.max(0, (naturalHeight * fit * this.scale - height) / 2);
    this.x = Math.max(-limitX, Math.min(limitX, this.x));
    this.y = Math.max(-limitY, Math.min(limitY, this.y));
    this.image.style.transform = `translate(${this.x}px, ${this.y}px) scale(${this.scale})`;
    this.stage.toggleAttribute('data-zoomed', this.scale > 1.01);
    this.querySelector('[data-viewer-scale]').textContent = `${Math.round(this.scale * 100)}%`;
    this.querySelector('[data-viewer-action="out"]').disabled = this.scale <= 1;
    this.querySelector('[data-viewer-action="in"]').disabled = this.scale >= 5;
  }

  pointerDown(event) {
    if (event.button !== 0) return;
    this.stage.setPointerCapture(event.pointerId);
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.pointers.size === 1) {
      this.gesture = { x: event.clientX, y: event.clientY, time: performance.now(), moved: false, pinched: false };
    } else {
      this.gesture.pinched = true;
      this.lastTap = null;
    }
    this.stage.toggleAttribute('data-dragging', this.scale > 1);
  }

  pointerMove(event) {
    const previous = this.pointers.get(event.pointerId);
    if (!previous) return;
    const before = [...this.pointers.values()];
    this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (Math.hypot(event.clientX - this.gesture.x, event.clientY - this.gesture.y) > 10) this.gesture.moved = true;
    const after = [...this.pointers.values()];
    if (after.length === 2) {
      const oldDistance = Math.hypot(before[0].x - before[1].x, before[0].y - before[1].y);
      const distance = Math.hypot(after[0].x - after[1].x, after[0].y - after[1].y);
      const oldCenter = { x: (before[0].x + before[1].x) / 2, y: (before[0].y + before[1].y) / 2 };
      const center = { x: (after[0].x + after[1].x) / 2, y: (after[0].y + after[1].y) / 2 };
      const bounds = this.stage.getBoundingClientRect();
      if (oldDistance > 0) this.setZoom(this.scale * distance / oldDistance,
        oldCenter.x - bounds.left - bounds.width / 2, oldCenter.y - bounds.top - bounds.height / 2);
      this.x += center.x - oldCenter.x;
      this.y += center.y - oldCenter.y;
      this.renderTransform();
    } else if (after.length === 1 && this.scale > 1) {
      this.x += event.clientX - previous.x;
      this.y += event.clientY - previous.y;
      this.renderTransform();
    }
  }

  pointerUp(event) {
    if (!this.pointers.has(event.pointerId)) return;
    this.pointers.delete(event.pointerId);
    if (this.pointers.size) return;
    this.stage.removeAttribute('data-dragging');
    const gesture = this.gesture;
    if (!gesture || gesture.pinched) return;
    const dx = event.clientX - gesture.x;
    const dy = event.clientY - gesture.y;
    const duration = performance.now() - gesture.time;
    if (gesture.moved) {
      this.lastTap = null;
      if (this.scale === 1 && Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.3 && duration < 800) {
        this.navigate(dx < 0 ? 1 : -1);
      }
      return;
    }
    const doubleTap = this.lastTap && performance.now() - this.lastTap.time < 300 &&
      Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 40;
    if (event.pointerType === 'mouse' || doubleTap) {
      const bounds = this.stage.getBoundingClientRect();
      this.setZoom(this.scale > 1 ? 1 : 2.5,
        event.clientX - bounds.left - bounds.width / 2,
        event.clientY - bounds.top - bounds.height / 2);
      this.lastTap = null;
    } else {
      this.lastTap = { time: performance.now(), x: event.clientX, y: event.clientY };
    }
  }

  cancelGesture() {
    this.pointers.clear();
    this.gesture = null;
    this.stage.removeAttribute('data-dragging');
  }

  finish() {
    this.loadingVersion = (this.loadingVersion || 0) + 1;
    this.cancelGesture();
    const item = this.items?.[this.index];
    if (this.group?.matches('media-gallery') && item?.dataset.mediaIndex !== undefined) {
      this.group.slideshow?.select(Number(item.dataset.mediaIndex), undefined, { animate: false });
      this.opener = item;
    }
    this.image.removeAttribute('src');
    this.opener?.focus({ preventScroll: true });
    if (this.scrollPosition) window.scrollTo({ left: this.scrollPosition.x, top: this.scrollPosition.y, behavior: 'instant' });
  }
}

if (!customElements.get('vyrloom-image-viewer')) {
  customElements.define('vyrloom-image-viewer', VyrloomImageViewer);
}

